import React, { useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { headerCellStyle, bodyCellStyle } from "./components/SelectableEnrollmentTable";

interface BulkUpdateTransportPlanModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  transportPlans: Array<{ id: number; name: string }>;
  onSuccess: () => void;
}

interface ResultRow {
  enrollmentId: number;
  status: "updated" | "skipped" | "not_found" | "error";
  reason?: string;
  error?: string;
}

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    updated:   { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:   { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found: { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:     { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkUpdateTransportPlanModal: React.FC<BulkUpdateTransportPlanModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  transportPlans,
  onSuccess,
}) => {
  // Per-enrollment chosen new transport plan (blank = "Select Plan").
  const [rowPlan, setRowPlan] = useState<Record<number, number | "">>({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  const rows = useMemo(() => {
    const nameByEnquiryId = new Map<number, string>();
    for (const s of studentList) nameByEnquiryId.set(s.enquiryId, s.name);
    const out: Array<{
      enrollmentId: number;
      name: string;
      code: string;
      oldPlan: string | null;
    }> = [];
    for (const eqId of selectedEnquiryIds) {
      for (const enr of enquiryEnrollmentsMap[eqId] || []) {
        if (enr?.enrollmentId) {
          out.push({
            enrollmentId: enr.enrollmentId,
            name: nameByEnquiryId.get(eqId) || "",
            code: enr.studentCode || "",
            oldPlan: enr.transportTemplateName ?? null,
          });
        }
      }
    }
    return out;
  }, [selectedEnquiryIds, enquiryEnrollmentsMap, studentList]);

  const setRow = (enrollmentId: number, v: number | "") =>
    setRowPlan((prev) => ({ ...prev, [enrollmentId]: v }));

  const pendingAssignments = useMemo(
    () =>
      rows
        .filter((r) => rowPlan[r.enrollmentId])
        .map((r) => ({ enrollmentId: r.enrollmentId, templateId: Number(rowPlan[r.enrollmentId]) })),
    [rows, rowPlan],
  );

  const handleClose = () => {
    setRowPlan({});
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (pendingAssignments.length === 0) {
      setError("Pick a transport plan for at least one student.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkUpdateTransportPlan({
        assignments: pendingAssignments,
      });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to update transport plan");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results ? "Update Transport Plan — Results" : "Update Transport Plan"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            {rows.length === 0 ? (
              <div
                style={{
                  padding: 16,
                  background: "#fefce8",
                  color: "#854d0e",
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                The selected students have no enrollments.
              </div>
            ) : (
              <div
                style={{
                  maxHeight: 320,
                  overflow: "auto",
                  border: "1px solid var(--border-default)",
                  borderRadius: 6,
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={headerCellStyle}>Name</th>
                      <th style={headerCellStyle}>Std Code</th>
                      <th style={headerCellStyle}>Old Transport Plan</th>
                      <th style={headerCellStyle}>New Transport Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.enrollmentId}>
                        <td style={bodyCellStyle}>
                          <span style={{ color: "var(--text-strong)" }}>{r.name}</span>
                        </td>
                        <td style={bodyCellStyle}>{r.code || "—"}</td>
                        <td style={bodyCellStyle}>
                          <span style={{ color: "var(--text-body)" }}>{r.oldPlan || "—"}</span>
                        </td>
                        <td style={bodyCellStyle}>
                          <Form.Select
                            size="sm"
                            value={rowPlan[r.enrollmentId] ?? ""}
                            onChange={(e) =>
                              setRow(r.enrollmentId, e.target.value ? Number(e.target.value) : "")
                            }
                            disabled={submitting}
                            style={{ minWidth: 180 }}
                          >
                            <option value="">Select Plan</option>
                            {transportPlans.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.updated ?? 0}</strong> updated
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "updated" && (
                          <span style={{ color: "var(--text-body)" }}>Transport plan updated</span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_assigned"
                              ? "Already on this plan"
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || pendingAssignments.length === 0}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Submitting…
                </>
              ) : (
                <>Submit</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkUpdateTransportPlanModal;
